/**
 * SimplifyResultCard — simplify skill 结果卡片
 *
 * 展示代码审查结果：问题列表、优化建议、修复代码。
 */

import { memo, useState } from 'react'
import { AlertTriangle, ChevronRight, Lightbulb } from 'lucide-react'
import { cn } from '@/lib/utils'
import './result-cards.less'

interface SimplifyResult {
  problems: string[]
  suggestions: string[]
  fixedCode?: string
  summary?: string
}

export const SimplifyResultCard = memo(function SimplifyResultCard({
  result,
}: {
  result: SimplifyResult
}) {
  const [showCode, setShowCode] = useState(false)

  return (
    <div className="flex flex-col gap-2">
      {result.summary && (
        <span className="text-[0.75rem] text-muted-foreground/70">{result.summary}</span>
      )}

      {result.problems.length > 0 && (
        <div className="result-card result-card--warning">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-amber-500/70" />
            <span className="text-[0.7rem] font-medium text-amber-600/80 dark:text-amber-400/80">
              发现 {result.problems.length} 个问题
            </span>
          </div>
          <ul className="flex flex-col gap-1 pl-[18px]">
            {result.problems.map((p) => (
              <li key={p} className="text-[0.7rem] text-muted-foreground/60 leading-relaxed list-disc">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.suggestions.length > 0 && (
        <div className="result-card result-card--success">
          <div className="flex items-center gap-1.5">
            <Lightbulb className="h-3 w-3 text-primary/70" />
            <span className="text-[0.7rem] font-medium text-primary/80">
              优化建议
            </span>
          </div>
          <ul className="flex flex-col gap-1 pl-[18px]">
            {result.suggestions.map((s) => (
              <li key={s} className="text-[0.7rem] text-muted-foreground/60 leading-relaxed list-disc">
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.fixedCode && (
        <button
          className="flex items-center gap-1 text-[0.7rem] text-primary/60 hover:text-primary/80 transition-colors w-fit"
          onClick={() => setShowCode(!showCode)}
          type="button"
        >
          <ChevronRight className={cn(
            'h-3 w-3 transition-transform duration-200',
            showCode && 'rotate-90',
          )} />
          <span>{showCode ? '收起修复代码' : '查看修复代码'}</span>
        </button>
      )}
      {result.fixedCode && showCode && (
        <div className="result-card__code-wrap">
          <pre className="result-card__code">{result.fixedCode}</pre>
        </div>
      )}
    </div>
  )
})
